"use client";

import {
    FeedRidContext,
    ResultsFeedContext,
    useWebsocketUpdates,
    WebsocketContext,
} from "@finbackoffice/site-core";
import { IClientError } from "@finbackoffice/websocket-client";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { EventStatus } from "@finbackoffice/enums";
import { FeedScopeEnum } from "utils/constants";
import { useDate, useSocketStorage } from "hooks";
import { SportResultsContext } from "contexts";
import { useGetCurrentFeedType } from "./useGetCurrentFeed";

interface ICalendarTreePayload {
    from: number;
    to: number;
}

export const useSportResults = () => {
    const { getTime, startOfDay, endOfDay } = useDate();
    const feed: FeedScopeEnum = useGetCurrentFeedType();
    const { setResults } = useContext(ResultsFeedContext);
    const { connected } = useContext(WebsocketContext);
    const { resultsRid } = useContext(FeedRidContext);
    const { getSports } = useSocketStorage(resultsRid);
    const { activeDate } = useContext(SportResultsContext);

    const handler = useCallback(
        (response: { rid: string }, error: IClientError) => {
            if (!error) {
                const rid = response.rid;
                const sports = getSports(rid);
                setResults({
                    rid,
                    loading: false,
                    sports,
                });
            } else {
                console.error(error);
            }
        },
        [getSports, setResults],
    );

    const payload: ICalendarTreePayload = useMemo(
        () => ({
            from: getTime(startOfDay(activeDate)),
            to: getTime(endOfDay(activeDate)),
            status: [EventStatus.Ended, EventStatus.Settled],
        }),
        [activeDate, endOfDay, getTime, startOfDay],
    );

    const { subscribe, unsubscribe } = useWebsocketUpdates(
        "calendar_tree",
        handler,
        { subscribeByDefault: false, statusesToDelete: [] },
        payload,
    );

    useEffect(() => {
        if (feed === FeedScopeEnum.results) {
            subscribe();
        } else {
            unsubscribe();
            setResults({
                rid: "",
                loading: true,
                sports: [],
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connected, feed, activeDate]);

    useEffect(() => {
        return () => {
            if (feed === FeedScopeEnum.results && activeDate) {
                setResults((state) => ({
                    ...state,
                    rid: "",
                    loading: true,
                    sports: [],
                }));
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeDate]);
};
